import "./ActionIcons.less";

import {
    GuxIcon,
    GuxButtonSlot,
    GuxTooltipBeta,
    GuxScreenReaderBeta,
} from "genesys-spark-components-react";
import strings from "./ActionIcons.strings";
import { useStrings } from "features/i18n/useStrings";
import { uniqueId } from "lodash";
import { HelpButton } from "./HelpButton";
import { GuxIconIconName } from "genesys-spark-components";
import { toGuxDisabled } from "util/componentUtil";
export function ActionIcon({
    iconName,
    text,
    performAction,
}: {
    iconName: GuxIconIconName;
    text: string;
    performAction?: () => void;
}) {
    const randomId = uniqueId();
    const isDisabled = toGuxDisabled(performAction === undefined);

    return (
        <GuxButtonSlot
            accent="ghost"
            icon-only
            className={`action-icon-${iconName}`}
        >
            <button
                type="button"
                id={randomId}
                onClick={performAction}
                disabled={isDisabled}
                data-testid={`${iconName}-icon-button`}
                aria-disabled={isDisabled}
            >
                <GuxIcon iconName={iconName} size="small" decorative></GuxIcon>
                <GuxScreenReaderBeta>{text}</GuxScreenReaderBeta>
            </button>
            {/* For visual purpose */}
            {!isDisabled && (
                <GuxTooltipBeta for={randomId} placement="bottom">
                    <div slot="content">{text}</div>
                </GuxTooltipBeta>
            )}
        </GuxButtonSlot>
    );
}

export function DeleteButton({ onDelete }: { onDelete?: () => void }) {
    const t = useStrings(strings);
    return (
        <ActionIcon
            iconName="delete"
            text={t.delete}
            performAction={onDelete}
        />
    );
}

export function EditButton({ onEdit }: { onEdit: () => void }) {
    const t = useStrings(strings);
    return <ActionIcon iconName="edit" text={t.edit} performAction={onEdit} />;
}

export function ActionButtons({
    helpText = undefined,
    onEdit,
    onDelete,
}: {
    helpText?: string;
    onEdit: () => void;
    onDelete?: () => void;
}) {
    return (
        <div className="ActionButtons">
            <HelpButton helpText={helpText} />
            <EditButton onEdit={onEdit} />
            <DeleteButton onDelete={onDelete} />
        </div>
    );
}

export function ReorderButtons({
    onMoveUp,
    onMoveDown,
}: {
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}) {
    const t = useStrings(strings);

    const handleMoveUp = onMoveUp
        ? () => {
              onMoveUp();
              requestAnimationFrame(() => {
                  (document.activeElement as HTMLElement)?.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "nearest",
                  });
              });
          }
        : undefined;

    const handleMoveDown = onMoveDown
        ? () => {
              onMoveDown();
              requestAnimationFrame(() => {
                  (document.activeElement as HTMLElement)?.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "nearest",
                  });
              });
          }
        : undefined;

    return (
        <div className="ReorderButtons" data-testid="reorder-buttons">
            <ActionIcon
                iconName="arrow-up"
                text={t.up}
                performAction={handleMoveUp}
            />
            <ActionIcon
                iconName="arrow-down"
                text={t.down}
                performAction={handleMoveDown}
            />
        </div>
    );
}
